/**
 * PastDueList - List of accounts with overdue payments
 *
 * Features:
 * - Days overdue badge with intensity scaling
 * - Amount due prominently displayed
 * - Quick action to view account
 * - Loading skeleton state
 */
import { cn } from '@respectly/ui';
import { Card, CardContent, CardHeader, CardTitle, Button, Skeleton } from '@respectly/ui';
import { Building2, Clock, DollarSign, ChevronRight, AlertCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import type { PastDueAccount } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface PastDueListProps {
  accounts: PastDueAccount[];
  onViewAccount?: (id: string) => void;
  onSendReminder?: (id: string) => void;
  isLoading?: boolean;
  className?: string;
}

// ============================================
// Formatters
// ============================================

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

// ============================================
// Days Overdue Badge
// ============================================

function DaysOverdueBadge({ days }: { days: number }) {
  // Intensity increases with days overdue
  const intensity =
    days >= 90
      ? 'bg-red-600 text-white'
      : days >= 60
        ? 'bg-red-500 text-white'
        : days >= 30
          ? 'bg-red-400 text-white'
          : days >= 14
            ? 'bg-orange-500 text-white'
            : 'bg-amber-500 text-white';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
        intensity
      )}
    >
      <Clock className="h-3 w-3" />
      {days}d overdue
    </span>
  );
}

// ============================================
// List Item
// ============================================

interface ListItemProps {
  account: PastDueAccount;
  onView?: () => void;
  index: number;
}

function PastDueListItem({ account, onView, index }: ListItemProps) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.2, delay: index * 0.05 }}
      className={cn(
        'flex items-center justify-between p-3 rounded-lg',
        'bg-red-50/50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20',
        'hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors cursor-pointer'
      )}
      onClick={onView}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {/* Account icon */}
        <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
          <Building2 className="h-4 w-4 text-red-600 dark:text-red-400" />
        </div>

        {/* Account info */}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
            {account.tenantName}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {account.planName}
            </span>
            <span className="text-slate-300 dark:text-slate-600">•</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {account.invoiceCount} invoice{account.invoiceCount !== 1 ? 's' : ''}
            </span>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Amount */}
        <div className="text-right">
          <p className="text-sm font-bold text-red-600 dark:text-red-400 tabular-nums">
            {formatCurrency(account.amountDue)}
          </p>
          <DaysOverdueBadge days={account.daysOverdue} />
        </div>

        {/* Arrow */}
        <ChevronRight className="h-4 w-4 text-slate-400" />
      </div>
    </motion.div>
  );
}

// ============================================
// Empty State
// ============================================

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="h-12 w-12 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center mb-3">
        <DollarSign className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
      </div>
      <p className="text-sm font-medium text-slate-900 dark:text-white">
        All caught up!
      </p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
        No accounts with overdue payments
      </p>
    </div>
  );
}

// ============================================
// Component
// ============================================

export function PastDueList({
  accounts,
  onViewAccount,
  isLoading,
  className,
}: PastDueListProps) {
  const totalOverdue = accounts.reduce((sum, acc) => sum + acc.amountDue, 0);

  if (isLoading) {
    return <PastDueListSkeleton className={className} />;
  }

  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-base font-semibold">Past Due Accounts</CardTitle>
            {accounts.length > 0 && (
              <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded-full bg-red-100 dark:bg-red-900/30 text-xs font-medium text-red-700 dark:text-red-400">
                {accounts.length}
              </span>
            )}
          </div>
          {totalOverdue > 0 && (
            <div className="flex items-center gap-1.5 text-sm">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <span className="font-semibold text-red-600 dark:text-red-400 tabular-nums">
                {formatCurrency(totalOverdue)}
              </span>
              <span className="text-slate-500">total</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {accounts.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-2">
            {accounts.slice(0, 5).map((account, index) => (
              <PastDueListItem
                key={account.id}
                account={account}
                onView={() => onViewAccount?.(account.tenantId)}
                index={index}
              />
            ))}
            {accounts.length > 5 && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-2 text-slate-600 hover:text-slate-900"
              >
                View all {accounts.length} accounts
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// Skeleton
// ============================================

export function PastDueListSkeleton({ className }: { className?: string }) {
  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60', className)}>
      <CardHeader className="pb-3">
        <div className="h-5 w-36 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="flex items-center justify-between p-3 rounded-lg bg-slate-100 dark:bg-slate-800"
          >
            <div className="flex items-center gap-3">
              <Skeleton className="h-9 w-9 rounded-lg" />
              <div>
                <Skeleton className="h-4 w-32 mb-1" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <div className="text-right">
              <Skeleton className="h-4 w-16 mb-1" />
              <Skeleton className="h-5 w-20" />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
